import { QueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { QueryKey, WorkflowExecutionFull, WorkflowExecutionNode } from '../model';
import { WorkflowExecutionStatusEnum } from 'types';
import { getWorkflowExecution, getWorkflowExecutionNodeStatus } from '../workflows/requests';
import { computeExecutionProgress, TERMINAL_EXECUTION_STATUSES, TERMINAL_NODE_STATUSES } from './executionProgress';
import { subscribe } from './subscribe';
import {
  ExecutionEvent,
  ExecutionSnapshot,
  ExecutionStatusEvent,
  NodeLogFileIdEvent,
  NodeStatusEvent,
  NodeSubworkflowOutputDataEvent,
  NodeTablesAddedEvent,
  NodeTokensEvent,
} from './types';

type ExecutionStreamEntry = {
  refCount: number;
  dispose: () => void;
  queryClient: QueryClient;
};

const EXECUTION_STREAM_ENTRIES = new Map<string, ExecutionStreamEntry>();
const FALLBACK_POLL_INTERVAL_MS = 3000;

const executionQueryKey = (id: string) => [QueryKey.WorkflowExecutions, { id }] as const;
const nodeStatusQueryKey = (executionId: string, nodeId: string) =>
  [QueryKey.WorkflowExecutionNodeStatus, executionId, nodeId] as const;

function patchExecution(
  queryClient: QueryClient,
  executionId: string,
  updater: (prev: WorkflowExecutionFull) => WorkflowExecutionFull,
  updateProgress = false,
) {
  queryClient.setQueryData<WorkflowExecutionFull>(executionQueryKey(executionId), (prev) => {
    if (!prev) return prev;
    const next = updater(prev);
    return { ...next, progress: updateProgress ? computeExecutionProgress(next.node_progress) : next.progress };
  });
}

function patchNode(
  queryClient: QueryClient,
  executionId: string,
  nodeId: string,
  updater: (node: WorkflowExecutionNode) => WorkflowExecutionNode,
  updateProgress = false,
) {
  patchExecution(
    queryClient,
    executionId,
    (prev) => ({
      ...prev,
      node_progress: prev.node_progress.map((n) => (n.node_id === nodeId ? updater(n) : n)),
    }),
    updateProgress,
  );
}

function applySnapshot(queryClient: QueryClient, executionId: string, snapshot: ExecutionSnapshot) {
  const execution = { ...snapshot };
  delete (execution as { kind?: 'snapshot' }).kind;
  queryClient.setQueryData<WorkflowExecutionFull>(executionQueryKey(executionId), execution as WorkflowExecutionFull);
}

function applyExecutionStatus(queryClient: QueryClient, executionId: string, event: ExecutionStatusEvent) {
  patchExecution(queryClient, executionId, (prev) => ({
    ...prev,
    status: event.status,
    end_time: event.end_time ?? prev.end_time,
    duration: event.duration ?? prev.duration,
  }));

  if (TERMINAL_EXECUTION_STATUSES.has(event.status)) {
    queryClient
      .fetchQuery({
        queryKey: executionQueryKey(executionId),
        queryFn: () => getWorkflowExecution(executionId),
        staleTime: 0,
      })
      .catch((err) => {
        console.warn('Failed to fetch full execution after terminal event', executionId, err);
      });
  }
}

function applyNodeStatus(queryClient: QueryClient, executionId: string, event: NodeStatusEvent) {
  if (event.result_truncated) {
    patchNode(
      queryClient,
      executionId,
      event.node_id,
      (node) => ({
        ...node,
        status: event.status,
        trigger_time: event.trigger_time ?? node.trigger_time,
        end_time: event.end_time ?? node.end_time,
        duration: event.duration ?? node.duration,
        items_count: event.items_count,
        processed_items_count: event.processed_items_count,
        processed_items_percent: event.processed_items_percent,
        log_revision: event.log_revision,
      }),
      true,
    );
    dispatchTruncationFetch(queryClient, executionId, event.node_id);
    return;
  }

  const isTerminal = TERMINAL_NODE_STATUSES.has(event.status);
  patchNode(
    queryClient,
    executionId,
    event.node_id,
    (prev) => {
      const node: WorkflowExecutionNode = {
        ...prev,
        status: event.status,
        trigger_time: event.trigger_time ?? prev.trigger_time,
        end_time: event.end_time ?? prev.end_time,
        duration: event.duration ?? prev.duration,
        items_count: event.items_count,
        processed_items_count: event.processed_items_count,
        processed_items_percent: event.processed_items_percent,
        log_revision: event.log_revision,
      };
      if (isTerminal) {
        node.result = {
          ...(prev.result ?? { project_id: '', execution_id: executionId, node_id: event.node_id }),
          ...(event.warnings !== undefined ? { warnings: event.warnings ?? undefined } : {}),
          ...(event.validation_error !== undefined ? { validation_error: event.validation_error ?? undefined } : {}),
          ...(event.export_file_id !== undefined ? { export_file_id: event.export_file_id ?? undefined } : {}),
        };
      }
      return node;
    },
    true,
  );
}

function applyNodeTokens(queryClient: QueryClient, executionId: string, event: NodeTokensEvent) {
  patchExecution(queryClient, executionId, (prev) => {
    const execution: WorkflowExecutionFull = {
      ...prev,
      token_usage: event.token_usage_metadata ?? prev.token_usage,
      node_progress: prev.node_progress.map((n) => {
        if (n.node_id !== event.node_id) return n;
        return {
          ...n,
          result: {
            ...(n.result ?? { project_id: '', execution_id: executionId, node_id: event.node_id }),
            token_usage_metadata: event.token_usage_metadata ?? undefined,
            node_token_usage_metadata: event.node_token_usage_metadata ?? undefined,
          },
        };
      }),
    };
    return execution;
  });
}

function applyNodeTablesAdded(queryClient: QueryClient, executionId: string, event: NodeTablesAddedEvent) {
  patchNode(queryClient, executionId, event.node_id, (node) => {
    const tables = [...(node.result?.tables ?? []), event.table];
    return {
      ...node,
      result: {
        ...(node.result ?? { project_id: '', execution_id: executionId, node_id: event.node_id }),
        tables,
      },
    };
  });
}

function applySubworkflowOutputData(
  queryClient: QueryClient,
  executionId: string,
  event: NodeSubworkflowOutputDataEvent,
) {
  patchNode(queryClient, executionId, event.node_id, (node) => ({
    ...node,
    result: {
      ...(node.result ?? { project_id: '', execution_id: executionId, node_id: event.node_id }),
      subworkflow_output_data: event.subworkflow_output_data,
    },
  }));
}

function applyNodeLogFileId(queryClient: QueryClient, executionId: string, event: NodeLogFileIdEvent) {
  patchNode(queryClient, executionId, event.node_id, (node) => ({
    ...node,
    result: {
      ...(node.result ?? { project_id: '', execution_id: executionId, node_id: event.node_id }),
      log_file_id: event.log_file_id,
    },
  }));
}

function dispatchTruncationFetch(queryClient: QueryClient, executionId: string, nodeId: string) {
  queryClient
    .fetchQuery({
      queryKey: nodeStatusQueryKey(executionId, nodeId),
      queryFn: () => getWorkflowExecutionNodeStatus(executionId, nodeId),
      staleTime: 0,
    })
    .then((node) => {
      patchNode(queryClient, executionId, nodeId, () => node);
    })
    .catch((err) => {
      console.warn('Failed to fetch full node status after truncation event', executionId, nodeId, err);
    });
}

export function refreshNodeStatus(queryClient: QueryClient, executionId: string, nodeId: string) {
  dispatchTruncationFetch(queryClient, executionId, nodeId);
}

function isExecutionTerminal(execution: WorkflowExecutionFull | undefined): boolean {
  return !!execution && TERMINAL_EXECUTION_STATUSES.has(execution.status as WorkflowExecutionStatusEnum);
}

export function isNodeTerminal(queryClient: QueryClient, executionId: string, nodeId: string): boolean {
  const execution = queryClient.getQueryData<WorkflowExecutionFull>(executionQueryKey(executionId));
  if (!execution) return false;
  if (isExecutionTerminal(execution)) return true;

  const node = execution.node_progress?.find((n) => n.node_id === nodeId);
  return !!node && TERMINAL_NODE_STATUSES.has(node.status);
}

export function watchNodeTerminal(
  queryClient: QueryClient,
  executionId: string,
  nodeId: string,
  onTerminal: () => void,
): () => void {
  let done = false;
  const unsubscribe = queryClient.getQueryCache().subscribe(() => {
    if (done) return;
    if (isNodeTerminal(queryClient, executionId, nodeId)) {
      done = true;
      unsubscribe();
      onTerminal();
    }
  });
  return unsubscribe;
}

function openStream(queryClient: QueryClient, executionId: string): () => void {
  let toasted = false;
  let pollTimer: ReturnType<typeof setInterval> | null = null;

  const startFallbackPolling = () => {
    if (pollTimer) return;
    pollTimer = setInterval(() => {
      void queryClient.invalidateQueries({ queryKey: executionQueryKey(executionId) });
    }, FALLBACK_POLL_INTERVAL_MS);
  };

  const releaseSubscribe = subscribe<ExecutionSnapshot, ExecutionEvent>(`/workflow-executions/${executionId}/events`, {
    onSnapshot(snapshot) {
      applySnapshot(queryClient, executionId, snapshot);
    },
    onEvent(event) {
      switch (event.kind) {
        case 'execution.status':
          applyExecutionStatus(queryClient, executionId, event);
          break;
        case 'node.status':
          applyNodeStatus(queryClient, executionId, event);
          break;
        case 'node.tokens':
          applyNodeTokens(queryClient, executionId, event);
          break;
        case 'node.tables.added':
          applyNodeTablesAdded(queryClient, executionId, event);
          break;
        case 'node.subworkflow_output_data':
          applySubworkflowOutputData(queryClient, executionId, event);
          break;
        case 'node.log_file_id':
          applyNodeLogFileId(queryClient, executionId, event);
          break;
      }
    },
    onResync() {
      void queryClient.invalidateQueries({ queryKey: executionQueryKey(executionId) });
    },
    onUnavailable() {
      // Realtime feature off on this env - silently fall back to polling.
      startFallbackPolling();
    },
    onFatal(err) {
      startFallbackPolling();
      if (toasted) return;
      toasted = true;
      console.warn('Execution stream failed', executionId, err);
      toast.warning('Live updates disconnected, falling back to polling.');
    },
    reconnectOnClose: false,
  });

  return () => {
    if (pollTimer) clearInterval(pollTimer);
    releaseSubscribe();
  };
}

export function acquireExecutionStream(queryClient: QueryClient, executionId: string): () => void {
  const cachedExecution = queryClient.getQueryData<WorkflowExecutionFull>(executionQueryKey(executionId));
  if (isExecutionTerminal(cachedExecution)) return () => undefined;

  let entry = EXECUTION_STREAM_ENTRIES.get(executionId);
  if (!entry) {
    entry = { refCount: 0, dispose: openStream(queryClient, executionId), queryClient };
    EXECUTION_STREAM_ENTRIES.set(executionId, entry);
  }
  entry.refCount += 1;

  let released = false;
  return () => {
    if (released) return;
    released = true;
    const entryToRelease = EXECUTION_STREAM_ENTRIES.get(executionId);
    if (!entryToRelease) return;
    entryToRelease.refCount -= 1;
    if (entryToRelease.refCount <= 0) {
      entryToRelease.dispose();
      EXECUTION_STREAM_ENTRIES.delete(executionId);
    }
  };
}
